import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';
import { submitPlayerScore } from '../adminApiService';
import HoleEventsTable from './HoleEventsTable';
import toast from 'react-hot-toast';
import './TournamentScoreEntryTab.css';

const TournamentScoreEntryTab = ({ 
  tournament, 
  tournamentPlayers = [], 
  canModify = false 
}) => {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    golferId: '',
    round: 1,
    hole: 1,
    score: '',
    par: 4
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form after successful submission
  const resetForm = () => {
    setFormData({
      golferId: '',
      round: 1,
      hole: 1,
      score: '',
      par: 4
    });
  };

  // Submit score mutation
  const submitScoreMutation = useMutation({
    mutationFn: ({ tournamentId, golferId, round, hole, score, par }) => 
      submitPlayerScore(tournamentId, golferId, round, hole, score, par),
    onSuccess: (data) => {
      toast.success(data.message);
      resetForm();
      // Invalidate leaderboard queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tournament-leaderboard', tournament.id] });
      queryClient.invalidateQueries({ queryKey: ['tournament-scores', tournament.id] });
      queryClient.invalidateQueries({ queryKey: ['hole-events', tournament.id] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update score');
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.golferId || !formData.score) {
      toast.error('Please select a player and enter a score');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitScoreMutation.mutateAsync({
        tournamentId: tournament.id,
        golferId: formData.golferId,
        round: parseInt(formData.round),
        hole: parseInt(formData.hole),
        score: parseInt(formData.score),
        par: parseInt(formData.par)
      });
    } catch (error) {
      // Error is handled by the mutation's onError
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate round options (1-4)
  const roundOptions = Array.from({ length: 4 }, (_, i) => i + 1);

  // Generate hole options (1-18)
  const holeOptions = Array.from({ length: 18 }, (_, i) => i + 1);

  // Get selected player info for display
  const selectedPlayer = tournamentPlayers.find(p => p.golfer.id === formData.golferId);

  return (
    <div className="admin-tournament-score-entry-tab">
      <div className="tab-header">
        <h2>Manual Score Entry</h2>
        <p>Add or update individual player scores for specific rounds and holes</p>
      </div>

      <div className="score-entry-content">
        <Card className="score-entry-form-section">
          <h3>Enter Score</h3>
          
          <form onSubmit={handleSubmit} className="score-entry-form">
            <div className="form-grid">
              {/* Player Selection */}
              <div className="A-form-group">
                <label htmlFor="golferId">Player *</label>
                <select
                  id="golferId"
                  value={formData.golferId}
                  onChange={(e) => handleInputChange('golferId', e.target.value)}
                  disabled={!canModify}
                  className="form-select"
                >
                  <option value="">Select a player</option>
                  {tournamentPlayers.map((player) => (
                    <option key={player.golfer.id} value={player.golfer.id}>
                      {player.golfer.name} ({player.golfer.country || 'N/A'})
                    </option>
                  ))}
                </select>
                {selectedPlayer && (
                  <div className="A-player-info">
                    <small>
                      <strong>Tier:</strong> {selectedPlayer.tier} | 
                      <strong> World Ranking:</strong> {selectedPlayer.golfer.worldRanking || 'N/A'}
                    </small>
                  </div>
                )}
              </div>

              {/* Round Selection */}
              <div className="A-form-group">
                <label htmlFor="round">Round *</label>
                <select
                  id="round"
                  value={formData.round}
                  onChange={(e) => handleInputChange('round', parseInt(e.target.value))}
                  disabled={!canModify}
                  className="form-select"
                >
                  {roundOptions.map(round => (
                    <option key={round} value={round}>
                      Round {round}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hole Selection */}
              <div className="A-form-group">
                <label htmlFor="hole">Hole *</label>
                <select
                  id="hole"
                  value={formData.hole}
                  onChange={(e) => handleInputChange('hole', parseInt(e.target.value))}
                  disabled={!canModify}
                  className="form-select"
                >
                  {holeOptions.map(hole => (
                    <option key={hole} value={hole}>
                      Hole {hole}
                    </option>
                  ))}
                </select>
              </div>

              {/* Score Input */}
              <div className="A-form-group">
                <label htmlFor="score">Score *</label>
                <Input
                  id="score"
                  type="number"
                  min="1"
                  max="15"
                  value={formData.score}
                  onChange={(e) => handleInputChange('score', e.target.value)}
                  placeholder="Enter score"
                  disabled={!canModify}
                  lightTheme={true}
                />
                <small className="A-form-help">Actual strokes taken on this hole</small>
              </div>

              {/* Par Input */}
              <div className="A-form-group">
                <label htmlFor="par">Par</label>
                <select
                  id="par"
                  value={formData.par}
                  onChange={(e) => handleInputChange('par', parseInt(e.target.value))}
                  disabled={!canModify}
                  className="form-select"
                >
                  <option value={3}>Par 3</option>
                  <option value={4}>Par 4</option>
                  <option value={5}>Par 5</option>
                </select>
                <small className="A-form-help">Par for this hole (defaults to 4)</small>
              </div>
            </div>

            {/* Score Summary */}
            {formData.score && formData.par && (
              <div className="score-summary">
                <div className="summary-card">
                  <h4>Score Summary</h4>
                  <div className="summary-details">
                    <div className="summary-item">
                      <span className="label">Score:</span>
                      <span className="value score-value">{formData.score}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Par:</span>
                      <span className="value par-value">{formData.par}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">To Par:</span>
                      <span className={`value to-par-value ${parseInt(formData.score) - parseInt(formData.par) > 0 ? 'over' : parseInt(formData.score) - parseInt(formData.par) < 0 ? 'under' : 'even'}`}>
                        {parseInt(formData.score) - parseInt(formData.par) > 0 ? '+' : ''}{parseInt(formData.score) - parseInt(formData.par)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="A-form-actions">
              <Button
                type="submit"
                disabled={!canModify || !formData.golferId || !formData.score || isSubmitting}
                className="submit-score-btn"
                variant="primary"
              >
                {isSubmitting ? 'Updating Score...' : 'Update Score'}
              </Button>
              
              <Button
                type="button"
                onClick={resetForm}
                disabled={!canModify || isSubmitting}
                variant="secondary"
                className="reset-form-btn"
              >
                Reset Form
              </Button>
            </div>
          </form>
        </Card>

        {/* Hole Events Tables */}
        <HoleEventsTable 
          tournamentId={tournament.id} 
          canModify={canModify}
        />

        {/* Tournament Status Warning */}
        {!canModify && (
          <Card className="status-warning">
            <h3>⚠️ Tournament Status</h3>
            <p>
              This tournament is in <strong>{tournament.status}</strong> status. 
              Score modifications are only allowed for DRAFT, ACTIVE, and LIVE tournaments.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TournamentScoreEntryTab;
